import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminClaimsApi } from "../../http/claims/api";
import { UpsertClaim } from "../../http/claims/types";
import ClaimAttachments from "../claimAttachments/ClaimAttachments";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import ClaimForm, { ClaimFormBody } from "./ClaimForm";

const EditClaim: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.claims.edit} />
      <TabsProvider>
        <TabbedEditClaim />
      </TabsProvider>
    </>
  );
};

export default EditClaim;

const TabbedEditClaim: FC = () => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const claimsApi = useAdminClaimsApi();
  const [isDragging, setIsDragging] = useState(false);
  const { id: claimId } = useParams<{ id: string }>();

  const [params] = useSearchParams();
  const carId = params.get("car_id");

  const claimSwr = claimsApi.getAdminClaim(claimId!, aborter.current.signal);
  const [claim, isInitialized] = useDataInitializer(claimSwr.data, claimSwr.error);

  useEffect(() => {
    claimId && claimSwr.trigger();
  }, [claimId]);

  useEffect(() => {
    claimSwr.error && dispatch(showErrorNotification(i18n, claimSwr.error));
  }, [claimSwr.error]);

  const onSubmit = async (formData: ClaimFormBody): Promise<void> => {
    if (claim?.id) {
      setIsSubmitting(true);

      const body: UpsertClaim = {
        carId: carId || formData.car?.id || "",
        driverId: formData.driver?.id ?? "",
        subsidiaryId: formData.subsidiary?.id ?? "",
        number: formData.number || null,
        status: formData.status.value,
        openingDate: formData.openingDate,
        closingDate: formData.closingDate,
        incidentDate: formData.incidentDate,
        typeId: formData.type?.id ?? "",
        description: formData.description,
        damageValueGross: formData.damageValueGross ?? undefined,
        damageValueNet: formData.damageValueNet ?? undefined,
        damageValueCurrency: formData.damageValueCurrency?.value,
        settledAmountGross: formData.settledAmountGross ?? undefined,
        settledAmountNet: formData.settledAmountNet ?? undefined,
        settledAmountCurrency: formData.settledAmountCurrency?.value,
        currentVersionNumber: claim.versionNumber,
      };

      try {
        await claimsApi.updateAdminClaim(claim.id, body);
        dispatch(showSuccessNotification(i18n.t));
        await claimSwr.trigger();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  const tabs = [{ title: i18n.t.common.generalData }, { title: i18n.t.common.documents }];

  const tabComponents: TabContent[] = [
    {
      body: (
        <ClaimForm
          onSubmit={onSubmit}
          isDisabled={!userSettings.permissions.claims.update || isSubmitting || !claim?.id}
          isLoading={!isInitialized}
          claim={claim}
          carId={carId}
        />
      ),
      withBorder: true,
      eager: true,
    },
    {
      body: <ClaimAttachments setIsDragging={setIsDragging} />,
      withBorder: true,
    },
  ];

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} isDragging={isDragging} />
    </>
  );
};
